import { observer, useLocalObservable } from 'mobx-react';
import { FUND_FIELDS } from '../../../../utils/constants/fields';
import { useStore } from '../../../../store/store';
import { formatNumberRange } from '../../../../utils/utils';
import IconButton, {
  ICON_BUTTON_TYPES,
  ICON_BUTTON_ICONS
} from '../../../../components/buttons/IconButton';
import ExistingOutreachPopup from '../../../../components/matchmaker/ExistingOutreachPopup';
import { useEffect } from 'react';
import ChipsList from '../../../../components/common/ChipsList';
import { isOutreachDisabled } from '../../../../utils/constants/variables';

const FundResult = observer(
  ({
    entry,
    displayDetailsPopup,
    displayMessagePopup,
    projectId,
    excludeResult,
    isInExcludeAction = false,
    setIsAnyOutreachButtonHovered
  }) => {
    const { utilsStore, outreachStore, projectStore } = useStore();
    const state = useLocalObservable(() => ({
      existingOutreachPopupOpen: false,
      toggleExistingOutreachPopup: () => {
        state.existingOutreachPopupOpen = !state.existingOutreachPopupOpen;
        utilsStore.lockScroll(state.existingOutreachPopupOpen);
      }
    }));

    useEffect(() => {
      return () => {
        if (state.existingOutreachPopupOpen) {
          state.toggleExistingOutreachPopup();
        }
      };
    }, [state]);

    const canOutreach =
      !!projectId &&
      !projectStore.isLoadingProjects &&
      !outreachStore.isSendingOutreach &&
      outreachStore.canSendOutreachToEntryId(projectId, entry.id);

    const disabledOutreach =
      !projectId || projectStore.isLoadingProjects || outreachStore.isSendingOutreach;

    const outreach = outreachStore.outreaches.find(
      (o) => o.projectId === projectId && o.objectData.id === entry.id
    );

    return (
      <div
        key={entry.id}
        className={`item funds-search-item ${entry.isExcluded ? 'no-shadow' : ''}`}>
        {state.existingOutreachPopupOpen && (
          <ExistingOutreachPopup onClose={state.toggleExistingOutreachPopup} outreach={outreach} />
        )}
        <div className="cols funds-search-cols">
          <div className="col name">
            <div className="label">Fund name</div>
            <div className="value">{entry[FUND_FIELDS.NAME.NAME] || entry.companyName}</div>
          </div>
          <div className="col industry-focus">
            <div className="label">Industry focus</div>
            <div className="value">
              {/* {utilsStore
                .shortenList(utilsStore.getOptionName(entry, FUND_FIELDS.INDUSTRIES), 3)
                .join(', ')} */}
              <ChipsList
                list={utilsStore.getOptionName(entry, FUND_FIELDS.INDUSTRIES)}
                alignTopMeasurementsEnabled
                alignTopMeasurementsMinResolutionWidth={833}
                alignTopMeasurementsRowQuerySelector=".page-search-listing > .listing-layout > .results > .item > .cols"
                alignTopMeasurementsItemsContainersQuerySelector="> .col"
              />
            </div>
          </div>
          <div className="col geo-focus">
            <div className="label">Geo focus</div>
            <div className="value">
              {/* {utilsStore
                .shortenList(utilsStore.getOptionName(entry, FUND_FIELDS.GEOGRAPHIES), 3)
                .join(', ')} */}
              <ChipsList list={utilsStore.getOptionName(entry, FUND_FIELDS.GEOGRAPHIES)} />
            </div>
          </div>
          <div className="col enterprise-value">
            <div className="label">Enterprise value (€M)</div>
            <div className="value">
              {formatNumberRange(
                entry[FUND_FIELDS.ENTERPRISE_RANGE_MIN.NAME],
                entry[FUND_FIELDS.ENTERPRISE_RANGE_MAX.NAME]
              )}
            </div>
          </div>
          <div className="col equity-value no-border">
            <div className="label">Equity value (€M)</div>
            <div className="value">
              {formatNumberRange(
                entry[FUND_FIELDS.EQUITY_RANGE_MIN.NAME],
                entry[FUND_FIELDS.EQUITY_RANGE_MAX.NAME]
              )}
            </div>
          </div>
          <div className="col no-desktop no-mobile">
            <div className="label">Stake</div>
            <div className="value">
              {utilsStore.getOptionName(entry, FUND_FIELDS.EQUITY).join(', ')}
            </div>
          </div>
          <div className="col no-desktop no-mobile no-border">
            <div className="label">Sales (€M)</div>
            <div className="value">
              {formatNumberRange(
                entry[FUND_FIELDS.REVENUE_RANGE_MIN.NAME],
                entry[FUND_FIELDS.REVENUE_RANGE_MAX.NAME]
              )}
            </div>
          </div>
          <div className="col no-border actions funds-search-actions">
            {entry.isExcluded ? (
              <IconButton
                wrapClassName="search-results-restore-btn"
                type={ICON_BUTTON_TYPES.BLUE}
                icon={ICON_BUTTON_ICONS.ARROW_COUNTERCLOCKWISE}
                innerText="Restore"
                onClick={() => excludeResult(entry, false)}
                disabled={isInExcludeAction}
                iconOnRight
                filled
              />
            ) : (
              <>
                <IconButton
                  wrapClassName="search-results-view-details-button desktop-only"
                  type={ICON_BUTTON_TYPES.BLUE}
                  onClick={() => displayDetailsPopup(entry)}
                  icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
                  tooltipText="View details"
                />
                <IconButton
                  wrapClassName="search-results-view-details-button mobile-only"
                  filled
                  type={ICON_BUTTON_TYPES.BLUE}
                  onClick={() => displayDetailsPopup(entry)}
                  innerText="View details"
                />
                <IconButton
                  onMouseEnter={() => setIsAnyOutreachButtonHovered?.(true)}
                  onMouseLeave={() => setIsAnyOutreachButtonHovered?.(false)}
                  wrapClassName="search-results-outreach-btn"
                  type={ICON_BUTTON_TYPES.BLUE}
                  icon={
                    outreachStore.canSendOutreachToEntryId(projectId, entry.id)
                      ? ICON_BUTTON_ICONS.CHAT_RIGHT_TEXT
                      : ICON_BUTTON_ICONS.ENVELOPE_CHECK
                  }
                  onClick={() => {
                    if (canOutreach) {
                      displayMessagePopup(entry);
                    } else {
                      if (outreach) {
                        state.toggleExistingOutreachPopup();
                      }
                    }
                  }}
                  disabled={isOutreachDisabled || disabledOutreach}
                  tooltipText={
                    isOutreachDisabled
                      ? 'Upcoming feature'
                      : !projectId
                        ? 'Save search results to initiate outreach'
                        : outreachStore.canSendOutreachToEntryId(projectId, entry.id)
                          ? 'Outreach'
                          : 'Outreach initiated'
                  }
                  tooltipPosition={
                    utilsStore.windowWidth > 1200
                      ? 'bottom-center'
                      : isOutreachDisabled
                        ? 'bottom-center'
                        : !projectId
                          ? 'bottom-end'
                          : 'bottom-center'
                  }
                  filled={!outreachStore.canSendOutreachToEntryId(projectId, entry.id)}
                />
                <IconButton
                  wrapClassName="search-results-exclude-btn"
                  type={ICON_BUTTON_TYPES.RED}
                  icon={ICON_BUTTON_ICONS.X_LG}
                  tooltipText="Exclude result"
                  tooltipPosition={utilsStore.windowWidth > 1200 ? 'bottom-center' : 'bottom-end'}
                  onClick={() => excludeResult(entry, true)}
                  disabled={isInExcludeAction}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default FundResult;
